import { Avatar, AvatarFallback } from "@/components/primitives/avatar";
import CachedAvatarImage from "@/components/shared/CachedAvatarImage";
import useUserAvatar from "@/middleware/hooks/useUserAvatar";
import { isDeletedUserLabel } from "@/lib/deletedSender";
import { cn, stringToRgbColor } from "@lib/utils";
import { UserX } from "lucide-react";
import { useMemo } from "react";

type UserAvatarProps = {
  username: string;
  fallbackAttachmentId?: string;
  className?: string;
  "aria-label"?: string;
};

export default function UserAvatar({
  username,
  fallbackAttachmentId,
  className,
  "aria-label": ariaLabel,
}: UserAvatarProps) {
  const avatarColor = useMemo(() => stringToRgbColor(username), [username]);
  const resolvedAttachmentId = useUserAvatar(username, fallbackAttachmentId);
  const resolvedAriaLabel = ariaLabel ?? `${username} avatar`;

  // Deleted account (read-time anonymisation): every deleted user shares the
  // same label, so hashed initials/colour would render all of them as one
  // identical fake identity ("DE" on one colour). Use a neutral glyph on a
  // muted background instead. This is the single chokepoint for every
  // user-avatar render (messages, quotes, threads, DMs, reactions, members).
  if (isDeletedUserLabel(username)) {
    return (
      <Avatar
        aria-label={resolvedAriaLabel}
        className={cn("size-8 bg-muted text-sm", className)}
      >
        <AvatarFallback className="bg-transparent text-muted-foreground">
          <UserX aria-hidden="true" className="size-4" />
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <Avatar
      aria-label={resolvedAriaLabel}
      style={{ backgroundColor: `rgba(${avatarColor}, 0.7)` }}
      className={cn("size-8 text-sm", className)}
    >
      <CachedAvatarImage attachmentId={resolvedAttachmentId} />
      <AvatarFallback className="bg-transparent text-white">
        {username.slice(0, 2).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}
